export type PlayerSlot = 1 | 2 | 3 | 4;
export type PlayerCount = 2 | 3 | 4;
export type Language = "cz" | "en";
export type MatchMode = "local" | "online";

export interface PlayerProfile {
  readonly id: string;
  readonly name: string;
  readonly color: number;
  readonly skin: number;
}

export interface PlayerInput {
  readonly left: boolean;
  readonly right: boolean;
  readonly up: boolean;
  readonly down: boolean;
  readonly shield: boolean;
  readonly rope: boolean;
  readonly ability: boolean;
}

export const EMPTY_INPUT: PlayerInput = Object.freeze({
  left: false,
  right: false,
  up: false,
  down: false,
  shield: false,
  rope: false,
  ability: false
});

export interface MatchRules {
  readonly playerCount: PlayerCount;
  readonly abilitiesCount: number;
  readonly abilityIds: readonly number[];
  readonly randomSkull: boolean;
  readonly fighterAbility: boolean;
  readonly winnerPoints: number;
  readonly showScore: boolean;
  readonly ropes: boolean;
  readonly shields: boolean;
  readonly physics: boolean;
}

export const DEFAULT_RULES: MatchRules = Object.freeze({
  playerCount: 2,
  abilitiesCount: 5,
  abilityIds: Object.freeze(Array.from({ length: 48 }, (_, index) => index + 1)),
  randomSkull: true,
  fighterAbility: true,
  winnerPoints: 3,
  showScore: true,
  ropes: true,
  shields: true,
  physics: false
});

export type WorldObjectKind =
  | "wall"
  | "wallLeft"
  | "wallRight"
  | "wallBottom"
  | "solidWall"
  | "kicker"
  | "horizontalKickerLeft"
  | "horizontalKickerRight"
  | "field"
  | "floatField"
  | "teleport"
  | "solidRope"
  | "solidRopeTop"
  | "kickEmitter"
  | "kickEmitterBottom"
  | "kickEmitterLeft"
  | "kickEmitterRight";

export interface WorldObjectDefinition {
  readonly id: string;
  readonly kind: WorldObjectKind;
  readonly sourceObject: string;
  readonly sprite: string | null;
  readonly x: number;
  readonly y: number;
  readonly targetX?: number;
  readonly targetY?: number;
  readonly emitterDelayTicks?: number;
  readonly emitterPeriodTicks?: number;
}

export interface ArenaMap {
  readonly id: string;
  readonly name: string;
  readonly sourceRoom: string | null;
  readonly width: number;
  readonly height: number;
  readonly playerCount: PlayerCount;
  readonly spawns: Readonly<Record<PlayerSlot, { readonly x: number; readonly y: number }>>;
  readonly objects: readonly WorldObjectDefinition[];
  readonly fixedAbilityIds: readonly number[] | null;
}

export type PlayerPose = "standing" | "running" | "falling" | "crouched" | "roping" | "rope" | "parachute" | "pistol" | "magnet";

export interface PlayerState {
  readonly slot: PlayerSlot;
  readonly profile: PlayerProfile;
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: -1 | 1;
  pose: PlayerPose;
  frame: number;
  alive: boolean;
  moveable: boolean;
  lockTicks: number;
  moving: boolean;
  crouched: boolean;
  onSolidRope: boolean;
  onRope: boolean;
  ropeId: string | null;
  canRope: boolean;
  kickCooldown: number;
  abilityId: number;
  abilityAmmo: number;
  abilityContinuous: boolean;
  invisible: boolean;
  frozenTicks: number;
  reverseTicks: number;
  noRopeTicks: number;
  hoverTicks: number;
  netTicks: number;
  teleportCooldown: number;
  pistolTicks: number;
  previousInput: PlayerInput;
}

export interface AbilityPickupState {
  readonly id: string;
  abilityId: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  lifetime: number;
}

export type ProjectileKind = "mine" | "clusterMine" | "grenade" | "clusterGrenade" | "flashGrenade" | "fireball" | "freezer" | "teleporter" | "hover" | "bomb" | "bomber" | "rocket" | "destroyer" | "runBomb" | "weasel" | "net" | "flame" | "pistol" | "laser" | "kickWave";

export interface ProjectileState {
  readonly id: string;
  readonly kind: ProjectileKind;
  readonly creator: PlayerSlot | null;
  x: number;
  y: number;
  vx: number;
  vy: number;
  lifetime: number;
  armedTicks: number;
  targetSlot: PlayerSlot | null;
  targetX: number | null;
  targetY: number | null;
  age: number;
  fuseTicks: number;
  counter: number;
  stuck: boolean;
}

export interface RopeState {
  readonly id: string;
  readonly owner: PlayerSlot;
  anchorX: number;
  anchorY: number;
  length: number;
  attached: boolean;
  vx: number;
  vy: number;
}

export type MatchPhase = "countdown" | "playing" | "roundOver" | "matchOver";

export interface MatchEvent {
  readonly tick: number;
  readonly type: "sound" | "death" | "pickup" | "roundWinner" | "matchWinner" | "teleport" | "explosion";
  readonly sound?: string;
  readonly player?: PlayerSlot;
  readonly x?: number;
  readonly y?: number;
}

export interface MatchState {
  readonly mode: MatchMode;
  readonly map: ArenaMap;
  readonly rules: MatchRules;
  tick: number;
  phase: MatchPhase;
  phaseTicks: number;
  rngState: number;
  nextEntityId: number;
  roomSpeed: number;
  roomSpeedTicks: number;
  flashTicks: number;
  abilitySpawnCooldown: number;
  abilityEmitterX: number;
  abilityEmitterDirection: -1 | 1;
  players: PlayerState[];
  pickups: AbilityPickupState[];
  projectiles: ProjectileState[];
  ropes: RopeState[];
  destroyedObjectIds: string[];
  temporaryWalls: WorldObjectDefinition[];
  scores: Record<PlayerSlot, number>;
  roundWinner: PlayerSlot | null;
  matchWinner: PlayerSlot | null;
  events: MatchEvent[];
}

export interface MatchOptions {
  readonly mode: MatchMode;
  readonly map: ArenaMap;
  readonly rules: MatchRules;
  readonly profiles: Readonly<Record<PlayerSlot, PlayerProfile>>;
  readonly seed: number;
}
